import TableMui from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { PAGE_INITIAL_VALUE, ROWS_PER_PAGE_OPTION } from 'common/constants';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { setFilterCurrentPage, setFilterRowsPerPage, setGroupList, setGroupName } from 'v3/data/store/group/actions';
import { IGroup, IGroupsList } from 'v3/data/store/group/types';
import { setAllGroups, setGroupAdminList } from 'v3/data/store/groupAdmin/actions';
import { showSnackbar } from 'v3/data/store/snackbar/actions';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import * as api from 'v3/services/api';
import { getGroupList, updateGroupsName } from 'v3/services/api';
import { CreateGroupListMobile } from 'v3/shared/components/MobileTable/CreateGroupMobileTable';
import { GenericNameModal } from 'v3/shared/components/Modal/GenericNameModal';
import { SubtitleMedium } from 'v3/shared/typography/SubtitleMedium';
import { NoItemsFound } from '../../NoItemsFound';
import {
  AlignStatusContainer,
  BoxCustom,
  EditIcon,
  PaperStyled,
  TableCellGroup,
  TableCellStyled,
  TableMobilePaginationStyles,
  TablePaginationStyles,
  TableRowStyled,
} from '../styles';

interface GroupsTableProps {
  groupList: IGroupsList;
}

export const GroupsTable: React.FC<GroupsTableProps> = ({ groupList }) => {
  const { isMobile } = useBreakpoints();
  const [openModal, setOpenModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<IGroup>();
  const { filter: groupAdminFilter } = useStoreSelector((store) => store.groupAdmin);

  const { filter, list } = useStoreSelector((store) => store.group);
  const { page, rowsPerPage } = filter;
  const { items, count } = list;

  const handleOpenModal = (group: IGroup) => {
    setSelectedGroup(group);
    setOpenModal(true);
  };

  const fetchGroupList = useCallback(async () => {
    try {
      const response = await getGroupList(filter);

      setGroupList(response.data);
    } catch (error) {
      showErrorDialog('Erro ao obter lista de grupos', error);
    }
  }, [filter]);

  const handleUpdateGroup = async (newName: string) => {
    if (!selectedGroup) return;

    const normalizedName = newName.trim().toLowerCase();

    const isDuplicate = list.items?.some((group) => group.name.trim().toLowerCase() === normalizedName);

    if (isDuplicate) {
      showSnackbar('Já existe um grupo com este nome', 'error', true);
      return;
    }

    try {
      const response = await updateGroupsName({
        id: selectedGroup.id,
        name: newName,
      });

      setGroupName(response.data);
      showSnackbar('Grupo editado com sucesso', 'success', true);
      setOpenModal(false);
      fetchGroupList();
      getGroupAdminList();
      getAllGroups();
    } catch (error) {
      showErrorDialog('Erro ao editar grupo', error);
    }
  };

  const getGroupAdminList = async () => {
    try {
      const response = await api.getGroupAdminList(groupAdminFilter);
      setGroupAdminList(response.data);
    } catch (error) {
      showErrorDialog('Erro ao obter lista de grupo admins', error);
    }
  };
  const getAllGroups = async () => {
    try {
      const response = await api.getAllStoreGroups();
      setAllGroups(response.data);
    } catch (error) {
      showErrorDialog('Erro ao buscar todos os grupos', error);
      console.error(error);
    }
  };

  useEffect(() => {
    fetchGroupList();
  }, [fetchGroupList]);

  if (!Array.isArray(items) || items.length === 0) {
    return <NoItemsFound />;
  }

  return (
    <Fragment>
      {!isMobile ? (
        <BoxCustom>
          <PaperStyled>
            <TableContainer sx={{ maxHeight: 450 }}>
              <TableMui stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    <TableCellStyled>Nome</TableCellStyled>
                    <TableCellStyled>Ações</TableCellStyled>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((group) => (
                    <TableRowStyled hover key={group.id}>
                      <TableCellGroup>
                        <SubtitleMedium>{group.name}</SubtitleMedium>
                      </TableCellGroup>
                      <TableCellGroup>
                        <AlignStatusContainer>
                          <SubtitleMedium>Editar</SubtitleMedium>
                          <EditIcon onClick={() => handleOpenModal(group)} />
                        </AlignStatusContainer>
                      </TableCellGroup>
                    </TableRowStyled>
                  ))}
                </TableBody>
              </TableMui>
            </TableContainer>
          </PaperStyled>

          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTION}
            component="div"
            sx={TablePaginationStyles}
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, value) => setFilterCurrentPage(value)}
            onRowsPerPageChange={(event) => {
              setFilterCurrentPage(PAGE_INITIAL_VALUE);
              setFilterRowsPerPage(Number(event.target.value));
            }}
          />
        </BoxCustom>
      ) : (
        <Fragment>
          <CreateGroupListMobile listItem={list} />
          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTION}
            component="div"
            sx={TableMobilePaginationStyles}
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            labelRowsPerPage="Cards por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            onPageChange={(event, value) => setFilterCurrentPage(value)}
            onRowsPerPageChange={(event) => {
              setFilterCurrentPage(PAGE_INITIAL_VALUE);
              setFilterRowsPerPage(Number(event.target.value));
            }}
          />
        </Fragment>
      )}

      <GenericNameModal
        title="Editar grupo"
        active={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleUpdateGroup}
        currentName={selectedGroup?.name}
      />
    </Fragment>
  );
};
